import { useState } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  StatCard,
  Card,
  CardContent,
  PageHeader,
  Spinner,
} from '@theonefamily/ui';
import {
  MapPin,
  Clock,
  Users,
  Truck,
  Calendar,
  CheckCircle,
  LogOut,
  Loader,
} from 'lucide-react';

interface VisitSummary {
  total_visits: number;
  avg_duration_minutes: number;
  unique_clients: number;
  unique_vehicles: number;
  visits: Visit[];
}

interface Visit {
  id: string;
  vehicle_name: string;
  client_name: string;
  geofence_name: string;
  arrived_at: string;
  departed_at: string | null;
  duration_minutes: number;
  status: string;
  auto_detected: boolean;
}

const MOCK_SUMMARY: VisitSummary = {
  total_visits: 24,
  avg_duration_minutes: 38,
  unique_clients: 12,
  unique_vehicles: 8,
  visits: [],
};

const STATUS_COLORS: Record<string, { bg: string; text: string; icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }> }> = {
  arrived: { bg: 'rgba(34,197,94,0.15)', text: '#22C55E', icon: CheckCircle },
  departed: { bg: 'rgba(156,163,175,0.15)', text: '#9CA3AF', icon: LogOut },
  in_progress: { bg: 'rgba(59,130,246,0.15)', text: '#3B82F6', icon: Loader },
};

export default function VisitLogPage() {
  const [dateFilter, setDateFilter] = useState(() => new Date().toISOString().slice(0, 10));
  const { data, isLoading } = useApi<VisitSummary>(`/api/visit-logs/summary?date=${dateFilter}`);
  const d = data ?? MOCK_SUMMARY;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Client Visits"
        description="Track vehicle visits to client locations"
        actions={
          <div className="flex items-center gap-3">
            {isLoading && <Spinner size={16} />}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-3 py-1.5 rounded-lg text-sm"
                style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--foreground)' }}
              />
            </div>
          </div>
        }
      />

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={MapPin} label="Total Visits" value={d.total_visits} accent="success" />
        <StatCard icon={Clock} label="Avg Duration" value={`${d.avg_duration_minutes} min`} />
        <StatCard icon={Users} label="Unique Clients" value={d.unique_clients} />
        <StatCard icon={Truck} label="Unique Vehicles" value={d.unique_vehicles} />
      </div>

      {/* Visit Log Table */}
      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b" style={{ borderColor: 'var(--glass-border)' }}>
            <h2 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>
              Visit Log
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                  {['Vehicle', 'Client', 'Geofence', 'Arrived', 'Departed', 'Duration', 'Status', 'Detection'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.visits.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>
                      No visits recorded for this date
                    </td>
                  </tr>
                ) : (
                  d.visits.map((v) => {
                    const sc = STATUS_COLORS[v.status] ?? { bg: 'rgba(156,163,175,0.15)', text: '#9CA3AF', icon: LogOut };
                    const SIcon = sc.icon;
                    return (
                      <tr key={v.id} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                        <td className="px-4 py-3" style={{ color: 'var(--foreground)' }}>{v.vehicle_name}</td>
                        <td className="px-4 py-3" style={{ color: 'var(--foreground)' }}>{v.client_name}</td>
                        <td className="px-4 py-3" style={{ color: 'var(--muted-foreground)' }}>{v.geofence_name}</td>
                        <td className="px-4 py-3 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                          {new Date(v.arrived_at).toLocaleTimeString()}
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                          {v.departed_at ? new Date(v.departed_at).toLocaleTimeString() : '—'}
                        </td>
                        <td className="px-4 py-3" style={{ color: 'var(--foreground)' }}>{v.duration_minutes} min</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: sc.bg, color: sc.text }}>
                            <SIcon className="w-3 h-3" />
                            {v.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {v.auto_detected && (
                            <span
                              className="px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981' }}
                            >
                              Auto
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
